<!-- 预约详情 -->
<template>
    <div class="page">
        <van-nav-bar
            title="预约详情"
            left-arrow
            fixed
            placeholder
            safe-area-inset-top
            @click-left="goBack"
        />

        <!-- 顶部状态区 -->
        <div class="hero" :class="heroClass">
            <div class="status-row">
                <i class="status-dot"></i>
                <span class="status-text">{{ statusText }}</span>
            </div>
            <div class="hero-time">{{ detail.date }} · {{ detail.timeRange }}</div>
            <div class="hero-title">{{ roomNameDisplay }}</div>
            <div class="hero-meta">{{ locationDisplay }}</div>
            <div class="hero-meta" style="font-size: 0.24rem;">受邀人数：{{ attendeeCount }}人</div>
        </div>

        <div class="body">
            <!-- 待审核提示 -->
            <div v-if="detail.status === 'pending'" class="notice notice-pending">
                <div class="notice-left">
                    <div class="notice-icon notice-icon-pending" aria-hidden="true">
                        <span class="pending-dots">
                            <i></i><i></i><i></i>
                        </span>
                    </div>
                    <div class="notice-copy">
                        <div class="notice-label">预约审核</div>
                        <div class="notice-title">等待项目管家审核</div>
                        <div class="notice-desc">审核通过前暂无开门权限。</div>
                    </div>
                </div>
                <button class="notice-btn notice-btn-pending" type="button" @click="contactManager">
                    联系项目管家
                </button>
            </div>

            <!-- 进行中：可开门 -->
            <div v-else-if="detail.status === 'ongoing'" class="notice notice-access">
                <div class="notice-left">
                    <div class="notice-icon notice-icon-access" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 11V8a5 5 0 019.9-1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <circle cx="12" cy="16" r="1.4" fill="currentColor"/>
                        </svg>
                    </div>
                    <div class="notice-copy">
                        <div class="notice-label">门禁权限</div>
                        <div class="notice-title">现在可以开门</div>
                        <div class="notice-desc">当前预约已通过审核，门禁权限有效</div>
                    </div>
                </div>
                <button class="notice-btn notice-btn-open" type="button" @click="openDoor">
                    开门
                </button>
            </div>

            <!-- 待使用：门禁未生效 -->
            <div v-else-if="detail.status === 'ready'" class="notice notice-access">
                <div class="notice-left">
                    <div class="notice-icon notice-icon-access" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 11V8a5 5 0 019.9-1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <circle cx="12" cy="16" r="1.4" fill="currentColor"/>
                        </svg>
                    </div>
                    <div class="notice-copy">
                        <div class="notice-label">门禁权限</div>
                        <div class="notice-title">开始前5分钟可开门</div>
                        <div class="notice-desc">门禁将在预约开始前自动生效</div>
                    </div>
                </div>
                <span class="access-tag">未生效</span>
            </div>

            <!-- 预约信息 -->
            <div class="info-card">
                <div class="info-title">预约信息</div>
                <div class="info-row">
                    <span class="info-label">预约时间</span>
                    <span class="info-value">{{ detail.date }} · {{ detail.timeRange }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">联系人电话</span>
                    <span class="info-value">{{ contactPhoneDisplay }}</span>
                </div>
                <div class="info-row info-row-wrap">
                    <span class="info-label">公司名称</span>
                    <span class="info-value">{{ rentCompanyNameDisplay }}</span>
                </div>
                <div class="info-row info-row-wrap">
                    <span class="info-label">公司室号</span>
                    <span class="info-value">{{ rentCompanyHouseNoDisplay }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">会议主题</span>
                    <span class="info-value">{{ meetingTitleDisplay }}</span>
                </div>
                <div class="info-row info-row-attendees">
                    <span class="info-label">参会人</span>
                    <div class="info-value info-value-stack">
                        <div
                            v-for="(name, index) in attendeeList"
                            :key="index"
                            class="attendee-line"
                        >
                            {{ name }}
                        </div>
                        <span v-if="!attendeeList.length" class="info-empty">—</span>
                    </div>
                </div>
                <div class="info-row" :class="{ 'is-last': detail.status !== 'finished' && detail.status !== 'rejected' }">
                    <span class="info-label">支付金额</span>
                    <span class="info-value">¥{{ payAmountText }}</span>
                </div>
                <div
                    v-if="detail.status === 'finished'"
                    class="info-row is-last"
                >
                    <span class="info-label">状态说明</span>
                    <span class="info-value">{{ statusRemark }}</span>
                </div>
                <div
                    v-if="detail.status === 'rejected'"
                    class="info-row is-last"
                >
                    <span class="info-label">拒绝原因</span>
                    <span class="info-value">{{ rejectReasonDisplay }}</span>
                </div>
            </div>

            <!-- 底部操作 -->
            <button
                v-if="showCancelBooking"
                class="cancel-btn"
                type="button"
                @click="cancelBooking"
            >
                取消预约
            </button>
        </div>

        <confirm-unlock
            v-model="unlockVisible"
            :reservation-id="detail.id"
            :room-name="detail.roomName"
            :floor="detail.floor"
        />
    </div>
</template>

<script>
import confirmUnlock from './confirmUnlock.vue'

function maskPhone(phone) {
    const text = String(phone || '')
    if (/^1\d{10}$/.test(text)) {
        return text.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2')
    }
    return text
}

function parseAttendeesList(attendees) {
    if (attendees == null || attendees === '') return []
    if (Array.isArray(attendees)) {
        return attendees.map((v) => String(v || '').trim()).filter(Boolean)
    }
    return String(attendees)
        .split(/[,，]/)
        .map((s) => s.trim())
        .filter(Boolean)
}

export default {
    name: 'bookingDetails',
    components: {
        confirmUnlock,
    },
    data() {
        return {
            unlockVisible: false,
            detail: {
                id: '',
                status: 'pending',
                cancelled: false,
                hasDoorAccess: false,
                bookingNo: '',
                date: '',
                timeRange: '',
                roomName: '',
                floor: '',
                buildingName: '',
                location: '',
                contactName: '',
                contactPhone: '',
                rentCompanyName: '',
                rentCompanyHouseNo: '',
                title: '',
                attendees: [],
                payAmount: 0,
                statusRemark: '',
                reason: '',
                filterTab: '',
                relation: '',
                adminPhone: '',
            },
        }
    },
    computed: {
        heroClass() {
            return {
                'hero-finished': this.detail.status === 'finished',
                'hero-rejected': this.detail.status === 'rejected',
            }
        },
        statusText() {
            const map = {
                pending: '待审核',
                ongoing: '会议进行中',
                ready: '待使用',
                finished: this.detail.cancelled ? '已结束 · 已取消' : '已结束',
                rejected: '已拒绝',
            }
            return map[this.detail.status] || ''
        },
        roomNameDisplay() {
            const name = this.detail.roomName || '会议室'
            const floor = String(this.detail.floor || '').trim()
            return floor ? `${name}(${floor})` : name
        },
        locationDisplay() {
            const building = String(this.detail.buildingName || '').trim()
            if (building) return building
            let loc = String(this.detail.location || '').trim()
            const floor = String(this.detail.floor || '').trim()
            if (loc && floor && loc.endsWith(`(${floor})`)) {
                loc = loc.slice(0, -(`(${floor})`.length)).trim()
            }
            return loc || '—'
        },
        contactPhoneDisplay() {
            const phone = maskPhone(this.detail.contactPhone)
            return phone || '—'
        },
        rentCompanyNameDisplay() {
            const name = String(this.detail.rentCompanyName || '').trim()
            return name || '—'
        },
        rentCompanyHouseNoDisplay() {
            const no = String(this.detail.rentCompanyHouseNo || '').trim()
            return no || '—'
        },
        meetingTitleDisplay() {
            const title = String(this.detail.title || '').trim()
            return title || '—'
        },
        attendeeList() {
            return parseAttendeesList(this.detail.attendees)
        },
        attendeeCount() {
            return this.attendeeList.length
        },
        payAmountText() {
            const amount = Number(this.detail.payAmount)
            if (Number.isNaN(amount)) return '0.00'
            return amount.toFixed(2)
        },
        statusRemark() {
            if (this.detail.statusRemark) return this.detail.statusRemark
            if (this.detail.cancelled) return '用户取消'
            return '已结束'
        },
        rejectReasonDisplay() {
            const reason = String(this.detail.reason || '').trim()
            return reason || '—'
        },
        showCancelBooking() {
            const isPendingTab = this.detail.filterTab === 'pending'
                || (!this.detail.filterTab && this.detail.status === 'pending')
            if (!isPendingTab) return false
            return this.detail.relation === '创建者'
        },
    },
    created() {
        this.loadDetail()
    },
    methods: {
        loadDetail() {
            let cached = null
            try {
                cached = JSON.parse(sessionStorage.getItem('bookingDetail') || 'null')
            } catch (e) {
                cached = null
            }

            const q = this.$route.query || {}
            const source = cached && String(cached.id) === String(q.id || cached.id)
                ? cached
                : null

            let userInfo = {}
            try {
                userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
            } catch (e) {
                userInfo = {}
            }

            const base = source || {}
            this.detail = {
                id: q.id || base.id || '',
                status: q.status || base.status || 'pending',
                cancelled: String(q.cancelled || base.cancelled || '') === 'true'
                    || String(q.cancelled || '') === '1'
                    || !!base.cancelled,
                hasDoorAccess: !!(base.hasDoorAccess || q.hasDoorAccess === '1'),
                bookingNo: q.bookingNo || base.bookingNo || '',
                date: q.date || base.date || '',
                timeRange: q.timeRange || base.timeRange || '',
                roomName: q.roomName || base.roomName || '',
                floor: base.floor || q.floor || '',
                buildingName: base.buildingName || q.buildingName || '',
                location: q.location || base.location || '',
                contactName: base.contactName || userInfo.userName || '陈先生',
                contactPhone: base.contactPhone || userInfo.phone || '',
                rentCompanyName: base.rentCompanyName || q.rentCompanyName || '',
                rentCompanyHouseNo: base.rentCompanyHouseNo || q.rentCompanyHouseNo || '',
                title: base.title || q.title || '',
                attendees: base.attendees != null ? base.attendees : '',
                payAmount: base.payAmount != null ? base.payAmount : 0,
                statusRemark: base.statusRemark || '',
                reason: String(base.reason || base.rejectReason || q.reason || '').trim(),
                filterTab: base.filterTab || '',
                relation: String(base.relation || '').trim(),
                adminPhone: base.adminPhone || '',
            }
        },
        goBack() {
            this.$router.back()
        },
        contactManager() {
            const phone = String(this.detail.adminPhone || '').trim()
            if (!phone) {
                this.$toast && this.$toast('暂无项目管家联系电话')
                return
            }
            window.location.href = `tel:${phone}`
        },
        openDoor() {
            if (this.detail.status !== 'ongoing') return
            this.unlockVisible = true
        },
        cancelBooking() {
            this.$dialog
                .confirm({
                    title: '取消预约',
                    message: `确认取消「${this.roomNameDisplay}」的预约吗？`,
                })
                .then(() => {
                    this.$toast && this.$toast('已取消预约')
                    this.$router.back()
                })
                .catch(() => {})
        },
    },
}
</script>

<style scoped>
.page {
    min-height: 100vh;
    background: #f2f4f8;
    box-sizing: border-box;
}

.page ::v-deep .van-nav-bar {
    background: #ffffff;
}

.page ::v-deep .van-nav-bar .van-nav-bar__title {
    color: #1e3a5f;
    font-weight: 600;
}

.page ::v-deep .van-nav-bar .van-icon {
    color: #2b7de9;
}

.hero {
    background: #0d2c54;
    padding: 0.28rem 0.36rem 0.4rem;
    box-sizing: border-box;
}

.hero-finished {
    background: #5d6b7d;
}

.hero-rejected {
    background: #7a3a3a;
}

.status-row {
    display: flex;
    align-items: center;
    margin-bottom: 0.2rem;
}

.status-dot {
    width: 0.14rem;
    height: 0.14rem;
    border-radius: 50%;
    background: #2fc97d;
    margin-right: 0.1rem;
    flex-shrink: 0;
}

.hero-finished .status-dot {
    background: #c5ccd6;
}

.hero-rejected .status-dot {
    background: #f0a8a8;
}

.status-text {
    font-size: 0.24rem;
    color: rgba(255, 255, 255, 0.92);
    line-height: 1.4;
}

.hero-time {
    font-size: 0.26rem;
    color: rgba(255, 255, 255, 0.78);
    line-height: 1.4;
}

.hero-title {
    margin-top: 0.1rem;
    font-size: 0.44rem;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.3;
}

.hero-meta {
    margin-top: 0.1rem;
    font-size: 0.26rem;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.4;
}

.body {
    padding: 0.24rem 0.28rem 0.48rem;
    box-sizing: border-box;
}

.notice {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.24rem;
    border-radius: 0.24rem;
    margin-bottom: 0.2rem;
    box-sizing: border-box;
}

.notice-pending {
    background: #fbf3e6;
}

.notice-access {
    background: #e9f8ef;
}

.notice-left {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    flex: 1;
    padding-right: 0.16rem;
}

.notice-icon {
    width: 0.72rem;
    height: 0.72rem;
    border-radius: 0.18rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 0.16rem;
}

.notice-icon-pending {
    background: #f4e4c8;
}

.pending-dots {
    display: flex;
    align-items: center;
    gap: 0.06rem;
}

.pending-dots i {
    width: 0.08rem;
    height: 0.08rem;
    border-radius: 50%;
    background: #c4923a;
}

.notice-icon-access {
    background: #d4f0e0;
    color: #1f9d63;
}

.notice-icon-access svg {
    width: 0.36rem;
    height: 0.36rem;
}

.notice-copy {
    min-width: 0;
}

.notice-label {
    font-size: 0.2rem;
    color: #b08a4a;
    line-height: 1.3;
}

.notice-access .notice-label {
    color: #3f9d6d;
}

.notice-title {
    margin-top: 0.04rem;
    font-size: 0.3rem;
    font-weight: 700;
    color: #3d2e16;
    line-height: 1.35;
}

.notice-access .notice-title {
    color: #1a4d35;
}

.notice-desc {
    margin-top: 0.04rem;
    font-size: 0.2rem;
    color: #a58b5c;
    line-height: 1.4;
}

.notice-access .notice-desc {
    color: #6ea887;
}

.notice-btn {
    flex-shrink: 0;
    border: none;
    cursor: pointer;
    white-space: nowrap;
}

.notice-btn-pending {
    height: 0.6rem;
    padding: 0 0.2rem;
    border-radius: 0.14rem;
    background: #c4923a;
    color: #ffffff;
    font-size: 0.22rem;
    font-weight: 500;
}

.notice-btn-pending:active {
    background: #b48330;
}

.notice-btn-open {
    min-width: 1.2rem;
    height: 0.64rem;
    padding: 0 0.28rem;
    border-radius: 0.16rem;
    background: #1f9d63;
    color: #ffffff;
    font-size: 0.26rem;
    font-weight: 600;
}

.notice-btn-open:active {
    background: #198854;
}

.access-tag {
    flex-shrink: 0;
    height: 0.56rem;
    padding: 0 0.22rem;
    border-radius: 0.14rem;
    background: #dce8e2;
    color: #7a9190;
    font-size: 0.22rem;
    line-height: 0.56rem;
    font-weight: 500;
}

.info-card {
    background: #ffffff;
    border-radius: 0.24rem;
    padding: 0.28rem 0.28rem 0.08rem;
    box-sizing: border-box;
    box-shadow: 0 0.04rem 0.16rem rgba(30, 58, 95, 0.04);
}

.info-title {
    font-size: 0.3rem;
    font-weight: 700;
    color: #1e3a5f;
    margin-bottom: 0.08rem;
    line-height: 1.4;
}

.info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 0.84rem;
    border-bottom: 1px solid #eef2f7;
    box-sizing: border-box;
}

.info-row.is-last {
    border-bottom: none;
}

.info-row-attendees {
    align-items: flex-start;
    padding: 0.22rem 0;
    min-height: 0.84rem;
}

.info-row-wrap {
    align-items: flex-start;
    padding: 0.22rem 0;
}

.info-row-wrap .info-value {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.45;
}

.info-value-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.1rem;
}

.attendee-line {
    font-size: 0.26rem;
    color: #1e3a5f;
    line-height: 1.45;
}

.info-empty {
    font-size: 0.26rem;
    color: #1e3a5f;
}

.info-label {
    font-size: 0.26rem;
    color: #8a9bb0;
    flex-shrink: 0;
}

.info-value {
    font-size: 0.26rem;
    color: #1e3a5f;
    text-align: right;
    margin-left: 0.2rem;
    word-break: break-all;
}

.cancel-btn {
    display: block;
    width: 100%;
    height: 0.88rem;
    margin-top: 0.28rem;
    border: 1px solid #f0b8b8;
    border-radius: 0.44rem;
    background: #ffffff;
    color: #e35d5d;
    font-size: 0.3rem;
    cursor: pointer;
}

.cancel-btn:active {
    background: #fff7f7;
}
</style>
